import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import {
  Observable,
  Subject,
  catchError,
  switchMap,
  take,
  throwError,
} from 'rxjs';

import { AuthService } from '../services/auth.service';

let isRefreshing = false;
const refreshComplete$ = new Subject<boolean>();

export function authInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Skip authentication for login and refresh endpoints
  if (isAuthEndpoint(request.url)) {
    return next(request);
  }

  // Add authorization header
  const authReq = addAuthHeader(request, authService);

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        if (isRefreshing) {
          return waitForRefresh(request, next, authService, router);
        }
        return handle401Error(request, next, authService, router);
      }
      return throwError(() => error);
    })
  );
}

/**
 * Add authorization header to request
 */
function addAuthHeader(request: HttpRequest<unknown>, authService: AuthService): HttpRequest<unknown> {
  const token = authService.getAccessToken();

  if (token) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
  return request;
}

/**
 * Handle 401 Unauthorized errors by attempting token refresh
 */
function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router
): Observable<HttpEvent<unknown>> {
  isRefreshing = true;

  return authService.refreshToken().pipe(
    switchMap(success => {
      isRefreshing = false;
      refreshComplete$.next(success);

      if (success) {
        const authReq = addAuthHeader(request, authService);
        return next(authReq);
      }
      redirectToLogin(authService, router);
      return throwError(() => new Error('Authentication failed'));
    }),
    catchError(error => {
      isRefreshing = false;
      refreshComplete$.next(false);
      redirectToLogin(authService, router);
      return throwError(() => error);
    })
  );
}

/**
 * Wait for ongoing refresh to complete, then retry request
 */
function waitForRefresh(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router
): Observable<HttpEvent<unknown>> {
  return refreshComplete$.pipe(
    take(1),
    switchMap(success => {
      if (success) {
        const authReq = addAuthHeader(request, authService);
        return next(authReq);
      }
      redirectToLogin(authService, router);
      return throwError(() => new Error('Authentication failed'));
    })
  );
}

/**
 * Save current URL as return target and log out
 */
function redirectToLogin(authService: AuthService, router: Router): void {
  const currentUrl = router.url;
  if (currentUrl && !currentUrl.startsWith('/login')) {
    authService.setReturnUrl(currentUrl);
  }
  authService.logout();
}

/**
 * Check if request is for authentication endpoints (no Bearer token needed)
 */
function isAuthEndpoint(url: string): boolean {
  try {
    const path = new URL(url, 'http://dummy').pathname;
    return (
      path === '/api/auth/' ||
      path === '/api/auth/sign_in/' ||
      path === '/api/auth/refresh/'
    );
  } catch {
    return (
      url.endsWith('/api/auth/') ||
      url.endsWith('/api/auth/sign_in/') ||
      url.endsWith('/api/auth/refresh/')
    );
  }
}
